import { z } from 'zod';
export * from './realtime.js';
export const pageQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(25),
});
// ─────────────────────────────────────────────────────────────────────────────
// Error codes
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The codes the dashboard and the Laravel client are allowed to branch on.
 * Anything not listed here is an unexpected failure and is rendered as one.
 */
export const ErrorCode = {
    Unauthenticated: 'unauthenticated',
    Forbidden: 'forbidden',
    NotFound: 'not_found',
    Validation: 'validation_failed',
    Conflict: 'conflict',
    RateLimited: 'rate_limited',
    // Checkout — each of these has a specific screen behind it.
    SessionExpired: 'checkout.session_expired',
    SessionLocked: 'checkout.session_locked',
    OtpInvalid: 'checkout.otp_invalid',
    OtpThrottled: 'checkout.otp_throttled',
    PincodeUnserviceable: 'checkout.pincode_unserviceable',
    CouponInvalid: 'checkout.coupon_invalid',
    // Billing — the merchant sees a top-up prompt rather than an error page.
    WalletInsufficient: 'billing.wallet_insufficient',
    BillingNotApproved: 'billing.not_approved',
    Internal: 'internal_error',
};
export const PLANS = {
    starter: {
        key: 'starter',
        name: 'Starter',
        description: 'Free to start — one store on each platform.',
        price: '0',
        features: [
            '3 stores — 1 Shopify, 1 WooCommerce, 1 custom site',
            '0.90% fee per order',
            'One-page checkout',
            'UPI, cards and netbanking',
            'COD with OTP verification',
            'Countdown timer',
            'Frequently bought together',
            'WhatsApp cart recovery',
            'Advanced analytics',
            'Email and chat support',
        ],
    },
    professional: {
        key: 'professional',
        name: 'Professional',
        description: 'Up to five stores with COD, recovery and analytics.',
        price: '999',
        features: [
            'Up to 5 stores on any platform',
            '0.70% fee per order',
            'COD with OTP verification',
            'Countdown timer',
            'Frequently bought together',
            'WhatsApp cart recovery',
            'Advanced analytics',
            'Priority email, chat and WhatsApp support',
        ],
    },
    scale: {
        key: 'scale',
        name: 'Scale',
        description: 'Everything, with support and reporting to match.',
        price: '4999',
        features: [
            'Everything in Professional',
            'Unlimited stores',
            '0.50% fee per order',
            'Countdown timer',
            'Frequently bought together',
            'Dedicated account manager with phone support',
            'Custom reports and data exports',
            'SLA-backed uptime',
            'Priority WhatsApp throughput',
        ],
    },
};
export const PLAN_LIMITS = {
    // Tiers differ by store count; every feature is on every plan. What an order
    // costs is the operator-set fee for the merchant's plan, falling back to the
    // platform-wide commission set in the admin console.
    starter: { stores: 3, storesPerPlatform: { shopify: 1, woocommerce: 1, custom: 1 }, cartRecovery: true },
    professional: { stores: 5, storesPerPlatform: null, cartRecovery: true },
    scale: { stores: null, storesPerPlatform: null, cartRecovery: true },
};
export function formatMoney(value, currency = 'INR', locale = 'en-IN') {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(Number(value));
}
// ─────────────────────────────────────────────────────────────────────────────
// Shared validators
// ─────────────────────────────────────────────────────────────────────────────
/** Bare 10-digit Indian national number — the same rule the Laravel Rule enforced. */
export const phoneSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => /^[6-9]\d{9}$/.test(v), 'Enter a valid 10-digit mobile number');
export const pincodeSchema = z
    .string()
    .trim()
    .refine((v) => /^[1-9]\d{5}$/.test(v), 'Enter a valid 6-digit PIN code');
export const otpSchema = z
    .string()
    .trim()
    .refine((v) => /^\d{4,6}$/.test(v), 'Enter the code from your message');
export const checkoutTokenSchema = z.string().length(32).regex(/^[a-z0-9]+$/);
//# sourceMappingURL=index.js.map